import { useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getDispute, postDisputeMessage, uploadDisputeEvidence,
  getDisputeEvidenceUrl, adminRequestDisputeEvidence,
  adminMoveDisputeUnderReview, adminCloseDisputeNoAction,
  adminResolveDisputeSeller,
} from "@/lib/disputes.functions";
import { refundOrderViaStripe } from "@/lib/stripe-connect.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatGBP } from "@/lib/format";
import { AlertOctagon, Paperclip, Send, ShieldCheck, Ban, FileText, Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/disputes/$disputeId")({
  head: () => ({ meta: [{ title: "Dispute — BidNova Auctions" }] }),
  component: DisputeDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

const REASON_LABELS: Record<string, string> = {
  item_not_received: "Item not received",
  item_not_as_described: "Item not as described",
  damaged_item: "Damaged item",
  wrong_item: "Wrong item sent",
  suspected_counterfeit: "Suspected counterfeit",
  missing_contents: "Missing contents",
  tracking_problem: "Tracking problem",
  other: "Other",
};
const RESOLUTION_LABELS: Record<string, string> = {
  full_refund: "Full refund",
  partial_refund: "Partial refund",
  replacement: "Replacement",
  other: "Other",
};
const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  awaiting_seller: "Awaiting seller",
  awaiting_buyer: "Awaiting buyer",
  under_review: "Under review",
  resolved_buyer: "Resolved (buyer)",
  resolved_seller: "Resolved (seller)",
  closed: "Closed",
};

const ACTIVE = new Set(["open", "awaiting_seller", "awaiting_buyer", "under_review"]);

function DisputeDetail() {
  const { t } = useTranslation("commerce");
  const { disputeId } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getDispute);
  const postMsgFn = useServerFn(postDisputeMessage);
  const uploadFn = useServerFn(uploadDisputeEvidence);
  const getUrlFn = useServerFn(getDisputeEvidenceUrl);
  const requestEvFn = useServerFn(adminRequestDisputeEvidence);
  const moveReviewFn = useServerFn(adminMoveDisputeUnderReview);
  const closeFn = useServerFn(adminCloseDisputeNoAction);
  const resolveSellerFn = useServerFn(adminResolveDisputeSeller);
  const refundFn = useServerFn(refundOrderViaStripe);

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [adminAction, setAdminAction] = useState<null | "close" | "seller" | "buyer">(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [refundAmountPounds, setRefundAmountPounds] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["disputes", "detail", disputeId],
    queryFn: () => getFn({ data: { disputeId } }),
  });

  const sendMsg = useMutation({
    mutationFn: () => postMsgFn({ data: { disputeId, message, isInternal } }),
    onSuccess: () => { setMessage(""); setIsInternal(false); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const buf = await file.arrayBuffer();
      // Base64 encode
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = btoa(bin);
      return uploadFn({
        data: {
          disputeId,
          fileName: file.name,
          mimeType: file.type as never,
          fileBase64: b64,
          caption: null,
        },
      });
    },
    onSuccess: () => { toast.success(t("toast.evidenceUploaded", "Evidence uploaded")); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const openEvidence = useMutation({
    mutationFn: (evidenceId: string) => getUrlFn({ data: { evidenceId } }),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener,noreferrer"),
    onError: (e: Error) => toast.error(e.message),
  });

  const adminAction_move = useMutation({
    mutationFn: () => moveReviewFn({ data: { disputeId, notes: adminNotes || null } }),
    onSuccess: () => { toast.success(t("toast.movedUnderReview", "Moved to under review")); setAdminNotes(""); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const adminAction_request = useMutation({
    mutationFn: (target: "buyer" | "seller") => requestEvFn({ data: { disputeId, target, notes: adminNotes || null } }),
    onSuccess: () => { toast.success(t("toast.evidenceRequested", "Evidence requested")); setAdminNotes(""); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const adminAction_close = useMutation({
    mutationFn: () => closeFn({ data: { disputeId, notes: adminNotes } }),
    onSuccess: () => { toast.success(t("toast.disputeClosed", "Dispute closed")); setAdminAction(null); setAdminNotes(""); qc.invalidateQueries({ queryKey: ["disputes"] }); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const adminAction_seller = useMutation({
    mutationFn: () => resolveSellerFn({ data: { disputeId, notes: adminNotes } }),
    onSuccess: () => { toast.success(t("toast.resolvedSeller", "Resolved in seller's favour — payout released")); setAdminAction(null); setAdminNotes(""); qc.invalidateQueries({ queryKey: ["disputes"] }); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const adminAction_buyer = useMutation({
    mutationFn: () => {
      const orderAmount = data?.dispute?.orders?.amount_pence ?? 0;
      const pence = refundAmountPounds.trim()
        ? Math.round(parseFloat(refundAmountPounds) * 100)
        : orderAmount;
      if (!pence || pence <= 0 || pence > orderAmount) throw new Error(t("toast.invalidRefundAmount", "Invalid refund amount"));
      return refundFn({ data: { orderId: data!.dispute!.order_id, disputeId, refundAmountPence: pence, notes: adminNotes } });
    },
    onSuccess: () => { toast.success(t("toast.resolvedBuyer", "Refund issued")); setAdminAction(null); setAdminNotes(""); setRefundAmountPounds(""); qc.invalidateQueries({ queryKey: ["disputes"] }); refetch(); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="mx-auto max-w-4xl px-4 py-12 text-muted-foreground">{t("disputeDetail.loading", "Loading…")}</div>;
  if (error) return <div className="mx-auto max-w-4xl px-4 py-12 text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const d = data.dispute as {
    id: string; order_id: string; buyer_id: string; seller_id: string; reason: string;
    description: string; requested_resolution: string; status: string;
    admin_resolution: string | null; resolution_notes: string | null;
    refund_amount_pence: number | null; created_at: string; resolved_at: string | null; closed_at: string | null;
    orders: {
      amount_pence: number; shipping_method: string | null; tracking_number: string | null;
      status: string; stripe_transfer_id: string | null; payout_released_at: string | null;
      auctions: { title: string; images: string[] | null } | null;
    } | null;
  };
  const role = data.role as "buyer" | "seller" | "admin";
  const isActive = ACTIVE.has(d.status);
  const canRespond = isActive && (role === "buyer" || role === "seller" || role === "admin");
  const orderAmount = d.orders?.amount_pence ?? 0;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to="/disputes" className="text-xs text-muted-foreground hover:text-foreground">{t("disputeDetail.back", "← All disputes")}</Link>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl">{d.orders?.auctions?.title ?? t("disputeDetail.orderFallback", "Order dispute")}</h1>
          <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/30">
            <AlertOctagon className="h-3 w-3 mr-1" />{t(`disputeStatus.${d.status}`, STATUS_LABELS[d.status] ?? d.status)}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground font-mono">{t("disputeDetail.reference", "Dispute #{disputeId} · Order #{orderId}", { disputeId: d.id.slice(0, 8), orderId: d.order_id.slice(0, 8) })}</p>
      </div>

      {/* Summary */}
      <div className="rounded-xl border border-border/60 p-5 grid gap-3 sm:grid-cols-2">
        <div className="flex items-start gap-3 sm:col-span-2">
          {d.orders?.auctions?.images?.[0] ? (
            <img src={d.orders.auctions.images[0]} alt="" className="h-16 w-16 rounded object-cover" />
          ) : <div className="h-16 w-16 rounded bg-surface-1" />}
          <div>
            <p className="font-medium">{d.orders?.auctions?.title}</p>
            <p className="text-sm text-muted-foreground">{t("disputeDetail.salePrice", "Sale price: {amount}", { amount: formatGBP(orderAmount) })}</p>
            {d.orders?.shipping_method && <p className="text-xs text-muted-foreground">{t("disputeDetail.shipping", "Shipping: {method}", { method: d.orders.shipping_method })}</p>}
            {d.orders?.tracking_number && <p className="text-xs text-muted-foreground">{t("disputeDetail.tracking", "Tracking: {number}", { number: d.orders.tracking_number })}</p>}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">{t("disputeDetail.reason", "Reason")}</p>
          <p>{t(`disputeReason.${d.reason}`, REASON_LABELS[d.reason] ?? d.reason)}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">{t("disputeDetail.requestedResolution", "Requested resolution")}</p>
          <p>{t(`disputeResolution.${d.requested_resolution}`, RESOLUTION_LABELS[d.requested_resolution] ?? d.requested_resolution)}</p>
        </div>
        <div className="sm:col-span-2">
          <p className="text-xs uppercase text-muted-foreground">{t("disputeDetail.description", "Description")}</p>
          <p className="whitespace-pre-wrap">{d.description}</p>
        </div>
        {d.admin_resolution && (
          <div className="sm:col-span-2 rounded-md border border-border/60 bg-surface-2/60 p-3">
            <p className="text-xs uppercase text-muted-foreground">{t("disputeDetail.adminResolution", "Admin resolution")}</p>
            <p className="font-medium">{d.admin_resolution}</p>
            {d.resolution_notes && <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{d.resolution_notes}</p>}
            {d.refund_amount_pence != null && <p className="text-sm">{t("disputeDetail.refund", "Refund: {amount}", { amount: formatGBP(d.refund_amount_pence) })}</p>}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="rounded-xl border border-border/60 p-5 space-y-4">
        <h2 className="font-display text-xl">{t("disputeDetail.conversation", "Conversation")}</h2>
        <ol className="space-y-3">
          {data.messages.map((m) => {
            const hidden = m.is_internal_admin_note && role !== "admin";
            if (hidden) return null;
            return (
              <li key={m.id} className="rounded-md border border-border/60 p-3 bg-surface-2/40">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium capitalize">{m.sender_role}</span>
                  <span>· {data.names[m.sender_user_id] ?? "User"}</span>
                  <span>· {new Date(m.created_at).toLocaleString("en-GB")}</span>
                  {m.is_internal_admin_note && <Badge variant="outline" className="ml-2">{t("disputeDetail.internalNote", "Internal note")}</Badge>}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{m.message}</p>
              </li>
            );
          })}
          {data.messages.length === 0 && <p className="text-sm text-muted-foreground">{t("disputeDetail.noMessages", "No messages yet.")}</p>}
        </ol>

        {canRespond && (
          <div className="space-y-2">
            <Textarea
              placeholder={t("disputeDetail.messagePlaceholder", "Write a message…")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={4000} rows={3}
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {role === "admin" && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                  {t("disputeDetail.internalNoteCheckbox", "Internal admin note (not visible to buyer or seller)")}
                </label>
              )}
              <div className="ml-auto flex gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload.mutate(f);
                    if (fileInput.current) fileInput.current.value = "";
                  }}
                />
                <Button variant="outline" size="sm" disabled={upload.isPending}
                  onClick={() => fileInput.current?.click()}>
                  {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Paperclip className="h-4 w-4 mr-1" />}
                  {t("disputeDetail.attachEvidence", "Attach evidence")}
                </Button>
                <Button size="sm" disabled={!message.trim() || sendMsg.isPending}
                  onClick={() => sendMsg.mutate()}>
                  <Send className="h-4 w-4 mr-1" /> {t("disputeDetail.send", "Send")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Evidence */}
      <div className="rounded-xl border border-border/60 p-5">
        <h2 className="font-display text-xl mb-3">{t("disputeDetail.evidence", "Evidence")}</h2>
        {data.evidence.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("disputeDetail.noEvidence", "No evidence uploaded yet.")}</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {data.evidence.map((e) => (
              <li key={e.id} className="flex items-center gap-3 rounded-md border border-border/60 p-3 bg-surface-2/40">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm">{e.file_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("disputeDetail.evidenceMeta", "{role} · {name} · {size} KB", { role: e.uploader_role, name: data.names[e.uploaded_by_user_id] ?? "User", size: (e.file_size / 1024).toFixed(0) })}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEvidence.mutate(e.id)}>{t("disputeDetail.open", "Open")}</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Admin actions */}
      {role === "admin" && isActive && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-5 space-y-3">
          <h2 className="font-display text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-red-400" /> {t("disputeDetail.adminActions", "Admin actions")}
          </h2>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button variant="outline" onClick={() => adminAction_request.mutate("buyer")}>{t("disputeDetail.askBuyerEvidence", "Ask buyer for more evidence")}</Button>
            <Button variant="outline" onClick={() => adminAction_request.mutate("seller")}>{t("disputeDetail.askSellerEvidence", "Ask seller for more evidence")}</Button>
            <Button variant="outline" onClick={() => adminAction_move.mutate()}>{t("disputeDetail.moveUnderReview", "Move to under review")}</Button>
            <Button variant="outline" onClick={() => { setAdminAction("close"); setAdminNotes(""); }}>{t("disputeDetail.closeNoAction", "Close without financial action")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => { setAdminAction("seller"); setAdminNotes(""); }}>{t("disputeDetail.resolveSellerFavour", "Resolve in seller's favour (release payout)")}</Button>
            <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => { setAdminAction("buyer"); setAdminNotes(""); setRefundAmountPounds(""); }}>{t("disputeDetail.resolveBuyerFavour", "Resolve in buyer's favour (refund)")}</Button>
          </div>
        </div>
      )}

      {/* Confirmation dialog for admin resolutions */}
      <Dialog open={!!adminAction} onOpenChange={(o) => { if (!o) { setAdminAction(null); setAdminNotes(""); setRefundAmountPounds(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {adminAction === "close" && t("disputeDetail.closeTitle", "Close dispute without financial action")}
              {adminAction === "seller" && t("disputeDetail.resolveSellerTitle", "Resolve in seller's favour")}
              {adminAction === "buyer" && t("disputeDetail.resolveBuyerTitle", "Resolve in buyer's favour (refund)")}
            </DialogTitle>
            <DialogDescription>
              {adminAction === "seller" && t("disputeDetail.resolveSellerDesc", "This will release the payout to the seller via Stripe.")}
              {adminAction === "buyer" && t("disputeDetail.resolveBuyerDesc", "This will reverse any prior seller transfer and refund the buyer.")}
              {adminAction === "close" && t("disputeDetail.closeDesc", "The order will proceed without any refund or transfer changes.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {adminAction === "buyer" && (
              <div>
                <Label>{t("disputeDetail.refundAmountLabel", "Refund amount (GBP) — leave blank for full refund of {amount}", { amount: formatGBP(orderAmount) })}</Label>
                <Input type="number" step="0.01" min="0" placeholder={(orderAmount / 100).toFixed(2)}
                  value={refundAmountPounds} onChange={(e) => setRefundAmountPounds(e.target.value)} />
              </div>
            )}
            <div>
              <Label>{t("disputeDetail.resolutionNotesLabel", "Resolution notes (required, min 5 chars)")}</Label>
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdminAction(null)}>{t("disputeDetail.cancel", "Cancel")}</Button>
            <Button
              disabled={
                adminNotes.trim().length < 5 ||
                adminAction_close.isPending || adminAction_seller.isPending || adminAction_buyer.isPending
              }
              onClick={() => {
                if (adminAction === "close") adminAction_close.mutate();
                else if (adminAction === "seller") adminAction_seller.mutate();
                else if (adminAction === "buyer") adminAction_buyer.mutate();
              }}
            >
              {(adminAction_close.isPending || adminAction_seller.isPending || adminAction_buyer.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              )}
              {t("disputeDetail.confirm", "Confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isActive && !d.admin_resolution && d.status === "closed" && (
        <div className="rounded-md border border-border/60 bg-surface-2/60 p-4 text-sm text-muted-foreground inline-flex items-center gap-2">
          <Ban className="h-4 w-4" /> {t("disputeDetail.closedNotice", "Dispute closed.")}
        </div>
      )}
    </div>
  );
}
